import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DATE_FORMATS = ["DD MMM YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "DD/MM/YYYY"];
const TIME_FORMATS = [
  { value: "24h", label: "24-hour (14:30)" },
  { value: "12h", label: "12-hour (2:30 PM)" },
];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
// Common IANA timezones for a South African firm — kept short & scannable.
const COMMON_TZ = [
  "Africa/Johannesburg", "Africa/Nairobi", "Africa/Lagos", "Africa/Cairo",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Zurich",
  "Asia/Dubai", "Asia/Singapore", "Asia/Kolkata", "Asia/Tokyo",
  "Australia/Sydney",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "UTC",
];

export default function GeneralTab({ settings, onSaved }) {
  const [tz, setTz] = useState(settings.timezone || "Africa/Johannesburg");
  const [df, setDf] = useState(settings.date_format || "DD MMM YYYY");
  const [tf, setTf] = useState(settings.time_format || "24h");
  const [fyMonth, setFyMonth] = useState(settings.fiscal_year_start_month || 3);
  const [fyDay, setFyDay] = useState(settings.fiscal_year_start_day || 1);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        timezone: tz.trim(),
        date_format: df,
        time_format: tf,
        fiscal_year_start_month: Number(fyMonth),
        fiscal_year_start_day: Math.min(28, Math.max(1, Number(fyDay))),
      });
      toast.success("General settings saved.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const fyEndMonth = ((Number(fyMonth) - 1 + 11) % 12) + 1;
  const fyLabel = `${MONTHS[fyMonth - 1]} ${fyDay} → ${MONTHS[fyEndMonth - 1]} ${fyDay === 1 ? 28 : fyDay - 1}`;

  return (
    <div data-testid="general-tab" className="space-y-8 max-w-2xl">
      <div>
        <div className="label-tracking mb-1">Locale</div>
        <h2 className="font-display text-2xl text-slate-900">General settings</h2>
        <p className="text-sm text-slate-500 mt-2">
          Controls how dates and times render across the app, and how quarters are calculated for reports.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Timezone (IANA)</label>
          <input
            data-testid="general-timezone"
            list="tz-options"
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-slate-900"
          />
          <datalist id="tz-options">
            {COMMON_TZ.map((z) => <option key={z} value={z} />)}
          </datalist>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Time format</label>
          <select
            data-testid="general-time-format"
            value={tf}
            onChange={(e) => setTf(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:border-slate-900"
          >
            {TIME_FORMATS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-600 mb-1">Date format</label>
          <select
            data-testid="general-date-format"
            value={df}
            onChange={(e) => setDf(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:border-slate-900"
          >
            {DATE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      <div className="pt-6 border-t border-slate-200">
        <div className="label-tracking mb-1">Fiscal year</div>
        <h3 className="font-display text-lg text-slate-900">Financial year start</h3>
        <p className="text-xs text-slate-500 mt-2 mb-4">
          Used by the Quarter date filter (This / Last quarter, Q1–Q4) in Analytics, Reports, and Leads.
        </p>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Start month</label>
            <select
              data-testid="general-fy-month"
              value={fyMonth}
              onChange={(e) => setFyMonth(Number(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:border-slate-900"
            >
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Start day</label>
            <Input
              data-testid="general-fy-day"
              type="number" min={1} max={28} value={fyDay}
              onChange={(e) => setFyDay(Number(e.target.value))}
              className="w-20"
            />
          </div>
          <div className="text-xs text-slate-500 pb-2" data-testid="general-fy-preview">
            <span className="font-medium text-slate-700">Fiscal year window:</span> {fyLabel}
          </div>
        </div>
      </div>

      <div className="pt-4">
        <Button
          data-testid="general-save-btn"
          onClick={save} disabled={saving}
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
